import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  Check,
  FileText,
  Percent,
  Scale,
  Search,
  User,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import type { Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { Cliente, CuentaDeServicio, Exoneracion, TipoDeExoneracion } from '@/shared/api/tipos';
import { AccionesDelFormulario, Campo, Modal, claseEntrada } from '@/shared/ui/Modal';
import { ComprobacionEnElRegistroCivil } from './ComprobacionEnElRegistroCivil';
import { notificar } from '@/shared/ui/avisos';

export function DialogoDeExoneracion({
  abierto,
  exoneracion,
  tipos,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;

  exoneracion: Exoneracion | null;
  tipos: TipoDeExoneracion[];
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const editando = exoneracion !== null;

  const [busqueda, setBusqueda] = useState('');
  const [cliente, setCliente] = useState<{
    id: number;
    nombre: string;
    identificacion: string;
  } | null>(
    exoneracion === null
      ? null
      : {
          id: exoneracion.clienteId,
          nombre: exoneracion.clienteNombre,
          identificacion: exoneracion.clienteIdentificacion,
        },
  );

  const [elegidas, setElegidas] = useState<number[]>(
    exoneracion?.cuentaId == null ? [] : [exoneracion.cuentaId],
  );
  const [tipo, setTipo] = useState(exoneracion?.tipo ?? '');
  const [porcentaje, setPorcentaje] = useState(
    exoneracion === null ? '' : String(exoneracion.porcentaje),
  );
  const [grado, setGrado] = useState(
    exoneracion?.gradoDiscapacidad == null ? '' : String(exoneracion.gradoDiscapacidad),
  );
  const [documento, setDocumento] = useState(exoneracion?.documentoRespaldo ?? '');
  const [baseLegal, setBaseLegal] = useState(exoneracion?.baseLegal ?? '');
  const [desde, setDesde] = useState(exoneracion?.fechaInicio ?? '');
  const [hasta, setHasta] = useState(exoneracion?.fechaFin ?? '');
  const [observaciones, setObservaciones] = useState(exoneracion?.observaciones ?? '');

  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const candidatos = useQuery({
    queryKey: ['clientes', 'exoneracion', busqueda],
    queryFn: () =>
      api.get<Pagina<Cliente>>('/clientes', {
        query: { texto: busqueda, tamanio: 6, activo: true },
      }),
    enabled: abierto && !editando && busqueda.trim().length >= 3,
  });

  const cuentas = useQuery({
    queryKey: ['clientes', cliente?.id, 'cuentas'],
    queryFn: () => api.get<CuentaDeServicio[]>(`/clientes/${cliente?.id}/cuentas`),
    enabled: abierto && cliente !== null,
  });

  const elegido = tipos.find((t) => t.codigo === tipo);

  function anotarCondicion(condicion: string) {
    const linea = `REGISTRO CIVIL: ${condicion}.`;
    setObservaciones((actuales) => {
      if (actuales.includes(condicion)) {
        return actuales;
      }
      return actuales.trim() === '' ? linea : `${actuales.trim()} ${linea}`;
    });
  }

  function elegirTipo(codigo: string) {
    setTipo(codigo);
    const t = tipos.find((x) => x.codigo === codigo);
    if (t) {
      setPorcentaje(String(t.porcentajePorOmision));
      setBaseLegal(t.baseLegal);
      if (!t.exigeGrado) {
        setGrado('');
      }
    }
  }

  const seAparta =
    elegido != null &&
    porcentaje !== '' &&
    Number.isFinite(Number(porcentaje)) &&
    Number(porcentaje) !== elegido.porcentajePorOmision;

  const listo =
    cliente !== null && tipo !== '' && porcentaje !== '' && desde !== '' && baseLegal.trim() !== '';

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    if (!listo || cliente === null) {
      return;
    }

    setError(null);
    setEnviando(true);
    try {
      const cuerpo = {
        clienteId: cliente.id,
        cuentaIds: elegidas,
        tipo,
        porcentaje: Number(porcentaje),
        gradoDiscapacidad: grado === '' ? null : Number(grado),
        documentoRespaldo: documento || null,
        baseLegal: baseLegal.trim(),
        fechaInicio: desde,
        fechaFin: hasta || null,
        observaciones: observaciones || null,
      };

      let cuantas = 1;
      if (editando) {
        await api.put<Exoneracion>(`/exoneraciones/${exoneracion.id}`, cuerpo);
      } else {

        cuantas = (await api.post<Exoneracion[]>('/exoneraciones', cuerpo)).length;
      }

      alGuardar();
      alCerrar();
      notificar.exito(
        editando
          ? `Exoneración actualizada: ${cliente.nombre}`
          : cuantas === 1
            ? `Solicitud registrada para ${cliente.nombre}`
            : `${cuantas} solicitudes registradas para ${cliente.nombre}`,
      );
    } catch (causa) {
      const mensaje =
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar la exoneración';
      setError(mensaje);
      notificar.error(mensaje);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={editando ? 'Editar exoneración' : 'Nueva exoneración'}
      subtitulo={
        editando ? `Editando: ${exoneracion.clienteNombre}` : 'Se registra pendiente de aprobación'
      }
      icono={Percent}
      ancho="amplio"
      cerrarAlPulsarFuera={false}
    >
      <form onSubmit={enviar} className="space-y-4">
        {error && (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
          >
            {error}
          </p>
        )}

        <Campo
          etiqueta="Beneficiario"
          requerido
          ayuda={
            editando
              ? 'NO SE PUEDE CAMBIAR: CONCEDÉRSELA A OTRA PERSONA ES OTRA EXONERACIÓN'
              : 'BUSQUE POR NOMBRE, CÉDULA/RUC O CORREO. DEBE SER EL TITULAR DEL SERVICIO.'
          }
        >
          {cliente ? (
            <div className="flex items-center gap-3 rounded-lg border border-marca-300 bg-marca-50 px-3 py-2.5">
              <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-white text-marca-600">
                {cliente.identificacion.length === 13 ? (
                  <Building2 className="size-4" aria-hidden />
                ) : (
                  <User className="size-4" aria-hidden />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold uppercase text-armazon-900">
                  {cliente.nombre}
                </span>
                <span className="importe block text-[11px] text-armazon-500">
                  {cliente.identificacion}
                </span>
              </span>
              {!editando && (
                <button
                  type="button"
                  onClick={() => {
                    setCliente(null);
                    setElegidas([]);
                    setBusqueda('');
                  }}
                  className="shrink-0 text-[11px] font-semibold uppercase text-marca-700 underline underline-offset-2"
                >
                  Cambiar
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3.5 top-1/2 size-[15px] -translate-y-1/2 text-armazon-400"
                  aria-hidden
                />
                <input
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  className={`${claseEntrada} pl-10`}
                  placeholder="Nombre, cédula o RUC…"
                  autoFocus
                />
              </div>

              {busqueda.trim().length >= 3 && (
                <div className="mt-2 max-h-44 overflow-y-auto rounded-lg border border-armazon-200">
                  {candidatos.isPending ? (
                    <p className="px-3 py-3 text-xs uppercase text-armazon-400">Buscando…</p>
                  ) : (candidatos.data?.contenido.length ?? 0) === 0 ? (
                    <p className="px-3 py-3 text-xs uppercase text-armazon-400">
                      Ningún cliente activo coincide
                    </p>
                  ) : (
                    <ul className="divide-y divide-armazon-100">
                      {(candidatos.data?.contenido ?? []).map((c) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            onClick={() =>
                              setCliente({
                                id: c.id,
                                nombre: c.nombre,
                                identificacion: c.identificacion,
                              })
                            }
                            className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-armazon-50"
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm uppercase text-armazon-800">
                                {c.nombre}
                              </span>
                              <span className="importe block text-[11px] text-armazon-500">
                                {c.identificacion}
                              </span>
                            </span>
                            <Check className="size-4 shrink-0 text-armazon-300" aria-hidden />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </Campo>

        {cliente && (
          <ComprobacionEnElRegistroCivil
            identificacion={cliente.identificacion}
            alConstarCondicion={anotarCondicion}
          />
        )}

        {cliente && (
          <Campo
            etiqueta="Predios a los que se aplica"
            ayuda="LA ORDENANZA LA CONCEDE AL TITULAR DEL SERVICIO: NORMALMENTE EL PREDIO DONDE VIVE, NO TODOS LOS QUE POSEE. SE REGISTRA UNA EXONERACIÓN POR CADA PREDIO ELEGIDO, PARA QUE CADA UNA SE APRUEBE Y CADUQUE POR SEPARADO."
          >
            <SelectorDePredios
              cuentas={cuentas.data ?? []}
              cargando={cuentas.isPending}
              elegidas={elegidas}
              alCambiar={setElegidas}
              bloqueado={editando}
            />
          </Campo>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Tipo de exoneración" requerido>
            <select
              value={tipo}
              onChange={(e) => elegirTipo(e.target.value)}
              className={claseEntrada}
              disabled={editando}
            >
              <option value="">SELECCIONAR</option>
              {tipos
                .filter((t) => t.activo || t.codigo === tipo)
                .map((t) => (
                  <option key={t.codigo} value={t.codigo}>
                    {t.nombre} · {t.porcentajePorOmision} %
                  </option>
                ))}
            </select>
          </Campo>

          <Campo
            etiqueta="Descuento"
            requerido
            ayuda={
              elegido == null
                ? 'LO QUE SE LE DEJA DE COBRAR'
                : `LA ORDENANZA CONCEDE EL ${elegido.porcentajePorOmision} % A ESTA CATEGORÍA`
            }
          >
            <div className="relative">
              <Percent
                className="pointer-events-none absolute left-3.5 top-1/2 size-[15px] -translate-y-1/2 text-armazon-400"
                aria-hidden
              />
              <input
                value={porcentaje}
                onChange={(e) => setPorcentaje(e.target.value)}
                type="number"
                step="0.01"
                min={0.01}
                max={100}
                className={`${claseEntrada} importe pl-10`}
                placeholder="50"
              />
            </div>
          </Campo>
        </div>

        {seAparta && elegido && (
          <div
            role="status"
            className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden />
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase text-amber-900">
                Se aparta de la ordenanza
              </p>
              <p className="mt-0.5 text-justify text-[11px] uppercase leading-relaxed text-amber-800">
                {elegido.nombre} concede el {elegido.porcentajePorOmision} %. Un descuento distinto
                necesita una resolución que lo respalde, y hay que escribirla en la base legal.
              </p>
            </div>
          </div>
        )}

        {elegido?.exigeGrado && (
          <div className="rounded-xl border border-armazon-200 bg-armazon-50/60 p-4">
            <Campo
              etiqueta="Grado de discapacidad del carné"
              requerido
              ayuda="ESTO NO ES EL DESCUENTO. EL DESCUENTO LO FIJA LA ORDENANZA; ESTO ES LO QUE DICE EL CARNÉ DEL CONADIS Y SE GUARDA COMO DATO."
            >
              <div className="relative">
                <Scale
                  className="pointer-events-none absolute left-3.5 top-1/2 size-[15px] -translate-y-1/2 text-armazon-400"
                  aria-hidden
                />
                <input
                  value={grado}
                  onChange={(e) => setGrado(e.target.value)}
                  type="number"
                  step="1"
                  min={30}
                  max={100}
                  className={`${claseEntrada} importe pl-10`}
                  placeholder="75"
                />
              </div>
            </Campo>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Documento de respaldo" ayuda="CARNÉ, CERTIFICADO MÉDICO, CÉDULA…">
            <div className="relative">
              <FileText
                className="pointer-events-none absolute left-3.5 top-1/2 size-[15px] -translate-y-1/2 text-armazon-400"
                aria-hidden
              />
              <input
                value={documento}
                onChange={(e) => setDocumento(e.target.value)}
                className={`${claseEntrada} pl-10`}
                placeholder="Carné CONADIS 1234567"
              />
            </div>
          </Campo>

          <Campo
            etiqueta="Base legal"
            requerido
            ayuda="LA NORMA QUE LA CONCEDE. SIN ELLA NO SE DEFIENDE EN UNA AUDITORÍA."
          >
            <input
              value={baseLegal}
              onChange={(e) => setBaseLegal(e.target.value)}
              className={claseEntrada}
              placeholder="Art. 12 literal a) — Ordenanza GIRS Latacunga"
            />
          </Campo>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Rige desde" requerido>
            <input
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              type="date"
              className={claseEntrada}
            />
          </Campo>

          <Campo
            etiqueta="Hasta"
            ayuda="VACÍO: NO CADUCA. LA TERCERA EDAD NO CADUCA; UN CARNÉ SÍ SE RENUEVA."
          >
            <input
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              type="date"
              min={desde || undefined}
              className={claseEntrada}
            />
          </Campo>
        </div>

        <Campo etiqueta="Observaciones">
          <textarea
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            rows={2}
            className={`${claseEntrada} resize-none`}
          />
        </Campo>

        <AccionesDelFormulario
          alCancelar={alCerrar}
          enviando={enviando || !listo}
          textoEnviar={editando ? 'Actualizar exoneración' : 'Registrar solicitud'}
        />
      </form>
    </Modal>
  );
}

function SelectorDePredios({
  cuentas,
  cargando,
  elegidas,
  alCambiar,
  bloqueado,
}: {
  cuentas: CuentaDeServicio[];
  cargando: boolean;
  elegidas: number[];
  alCambiar: (cuentas: number[]) => void;
  bloqueado: boolean;
}) {
  if (cargando) {
    return (
      <p className="rounded-lg border border-armazon-200 px-3 py-2.5 text-xs uppercase text-armazon-400">
        Buscando sus predios…
      </p>
    );
  }

  if (cuentas.length === 0) {
    return (
      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs uppercase text-amber-800">
        Este titular no tiene ninguna cuenta activa. La exoneración se registrará como general y no
        descontará nada hasta que tenga un predio.
      </p>
    );
  }

  const todas = elegidas.length === 0;

  function alternar(id: number) {
    alCambiar(elegidas.includes(id) ? elegidas.filter((x) => x !== id) : [...elegidas, id]);
  }

  return (
    <div className="overflow-hidden rounded-lg border border-armazon-200">
      <button
        type="button"
        onClick={() => alCambiar([])}
        disabled={bloqueado}
        className={`flex w-full items-center gap-2.5 border-b border-armazon-200 px-3 py-2.5 text-left transition-colors disabled:cursor-not-allowed ${
          todas ? 'bg-marca-50' : 'hover:bg-armazon-50'
        }`}
      >
        <Marca marcada={todas} />
        <span className="min-w-0">
          <span className="block text-xs font-semibold uppercase text-armazon-800">
            Todas sus cuentas
          </span>
          <span className="block text-[11px] text-armazon-500">
            Una sola exoneración que cubre las {cuentas.length} y las que se den de alta después
          </span>
        </span>
      </button>

      <ul className="max-h-48 overflow-y-auto">
        {cuentas.map((c) => {
          const marcada = elegidas.includes(c.id);
          return (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => alternar(c.id)}
                disabled={bloqueado}
                className={`flex w-full items-center gap-2.5 border-b border-armazon-100 px-3 py-2 text-left transition-colors last:border-b-0 disabled:cursor-not-allowed ${
                  marcada ? 'bg-marca-50' : 'hover:bg-armazon-50'
                }`}
              >
                <Marca marcada={marcada} />
                <span className="min-w-0">
                  <span className="importe block truncate text-xs text-armazon-800">
                    {c.numeroCuenta}
                  </span>
                  {c.direccionServicio && (
                    <span className="block truncate text-[11px] uppercase text-armazon-500">
                      {c.direccionServicio}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <p className="border-t border-armazon-200 bg-armazon-50 px-3 py-2 text-[11px] uppercase text-armazon-500">
        {todas
          ? 'Se registrará una exoneración general'
          : elegidas.length === 1
            ? 'Se registrará una exoneración, sobre ese predio'
            : `Se registrarán ${elegidas.length} exoneraciones, una por predio`}
      </p>
    </div>
  );
}

function Marca({ marcada }: { marcada: boolean }) {
  return (
    <span
      aria-hidden
      className={`grid size-4 shrink-0 place-items-center rounded border transition-colors ${
        marcada ? 'border-marca-500 bg-marca-500 text-white' : 'border-armazon-300 bg-white'
      }`}
    >
      {marcada && <Check className="size-3" />}
    </span>
  );
}
